import { API_CONFIG, getPangoHeaders, getPangoUrl } from '../config/api';

// Types
export interface LoginRequest {
  outlet_id: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    outlet: {
      outlet_id: string;
      name: string;
      outlet_type: string;
      region: string;
      contact_person: string;
      phone: string;
      city_tier: string;
      store_size: string;
      customer_volume: string;
      operating_hours: string;
      customer_type: string;
      scoring: {
        erfm_score: number;
        mission_score: number;
        order_score: number;
        performance_score: number;
        overall_score: number;
        score_tier: string;
      };
    };
    authentication: {
      token: string;
      expires_at: string;
      last_login: string;
      login_source: string;
      login_count: number | null;
    };
  };
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface ValidateTokenResponse {
  success: boolean;
  message: string;
  data: {
    is_valid: boolean;
    outlet_id?: string;
    expires_at?: string;
  };
}

// TODO: Implement other interfaces when needed
// - Product interface for catalog
// - Order interface for smart orders  
// - Promotion interface for promotions
// - Mission interface for missions

// API Service Class
export class PangoApiService {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = API_CONFIG.PANGO.BASE_URL;
    this.apiKey = API_CONFIG.PANGO.API_KEY;
  }

  // Generic API call method
  private async apiCall<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = getPangoUrl(endpoint);
    const headers = getPangoHeaders();

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Pango API Error:', error);
      
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Không thể kết nối đến máy chủ. Kiểm tra kết nối mạng.');
      }
      
      // Re-throw with original error message
      throw error;
    }
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.apiCall<LoginResponse>(
      API_CONFIG.PANGO.ENDPOINTS.AUTH.LOGIN,
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    );
  }

  async logout(token: string, outletId: string): Promise<LogoutResponse> {
    // Validate required parameters
    if (!token || !outletId) {
      throw new Error('Thiếu thông tin outlet_id hoặc token');
    }

    return this.apiCall<LogoutResponse>(
      API_CONFIG.PANGO.ENDPOINTS.AUTH.LOGOUT,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-Token': token,
          'X-Outlet-ID': outletId
        },
        body: JSON.stringify({ outlet_id: outletId })
      }
    );
  }

  async validateToken(token: string, outletId: string): Promise<ValidateTokenResponse> {
    return this.apiCall<ValidateTokenResponse>(
      API_CONFIG.PANGO.ENDPOINTS.AUTH.VALIDATE,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ outlet_id: outletId })
      }
    );
  }

  // TODO: Implement other API methods when needed
  // - Products API
  // - Smart Orders API  
  // - Missions API
  // - Promotions API
  // - Outlets API
}

// Export singleton instance
export const pangoApi = new PangoApiService();
